import { Component, Inject, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NoteService } from '../../../core/services/note.service';
import { ClientNoteForUpdation } from '../../../core/models/notes/client-note-for-updation.model';
import { EditorComponent } from '@tinymce/tinymce-angular';

declare var tinymce: any;

export interface ClientNotesModalData {
  clientId: number;
  clientName?: string;
}

@Component({
  selector: 'app-client-notes-modal',
  templateUrl: './client-notes-modal.component.html',
  styleUrls: ['./client-notes-modal.component.css'],
  standalone: false,
})
export class ClientNotesModalComponent implements OnInit, AfterViewInit {
  @ViewChild('editor') editor!: EditorComponent;
  notesForm!: FormGroup;
  isSubmitting = false;
  isLoading = false;
  isMaximized = false;
  initialContent = '';
  private editorInstance: any = null;

  tinymceInit: any = {
    base_url: '/assets/tinymce',
    suffix: '.min',
    height: 'calc(95vh - 160px)',
    menubar: 'file edit view insert format tools table help',
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount'
    ],
    toolbar:
    'undo redo | blocks | ' +
    'fullscreen | ' +
    'bold italic forecolor underline strikethrough | ' +
    'alignleft aligncenter alignright alignjustify | ' +
    'bullist numlist outdent indent | ' +
    'link image media table charmap insertdatetime | ' +
    'searchreplace visualblocks code preview  removeformat help',
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
    branding: false,
    promotion: false,
    skin: 'oxide',
    content_css: 'default',
    setup: (editor: any) => {
      this.editorInstance = editor;
    }
  };

  constructor(
    private fb: FormBuilder,
    private noteService: NoteService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<ClientNotesModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClientNotesModalData
  ) { 
    // Configure TinyMCE globally to use local installation
    if (typeof window !== 'undefined' && (window as any).tinymce) {
      (window as any).tinymce.baseURL = '/assets/tinymce';
      (window as any).tinymce.suffix = '.min';
    }
  }

  ngOnInit(): void {
    this.buildForm();
    this.loadNotes();
    // Set dialog to maximized size on init
    this.dialogRef.updateSize('95vw', '95vh');
  }

  ngAfterViewInit(): void {
    // Ensure TinyMCE is configured after view init
    if (typeof window !== 'undefined' && (window as any).tinymce) {
      (window as any).tinymce.baseURL = '/assets/tinymce';
      (window as any).tinymce.suffix = '.min';
    }
    // Trigger TinyMCE resize after a short delay since dialog opens maximized
    setTimeout(() => {
      if (this.editorInstance) {
        this.editorInstance.execCommand('mceAutoResize');
      }
    }, 300);
  }

  buildForm(): void {
    this.notesForm = this.fb.group({
      notes: ['']
    });
  }

  loadNotes(): void {
    this.isLoading = true;
    this.noteService.getNoteByClientId(this.data.clientId).subscribe({
      next: (note) => {
        const notesContent = note.notes || '';
        this.initialContent = notesContent;
        this.notesForm.patchValue({ notes: notesContent });
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        // If no notes exist, that's okay - just start with empty content
        this.initialContent = '';
      }
    });
  }

  onSave(): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    const notesContent = this.notesForm.value.notes || '';

    const updateData: ClientNoteForUpdation = {
      clientId: this.data.clientId,
      notes: notesContent
    };

    this.noteService.updateClientNotes(updateData).subscribe({
      next: () => {
        this.snackBar.open('Notes saved successfully', 'Close', { duration: 3000 });
        this.initialContent = notesContent;
        this.isSubmitting = false;
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackBar.open('Error saving notes. Please try again.', 'Close', { duration: 3000 });
        this.isSubmitting = false;
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  toggleMaximize(): void {
    this.isMaximized = !this.isMaximized;
    // Update dialog size
    if (this.isMaximized) {
      this.dialogRef.updateSize('95vw', '95vh');
    } else {
      this.dialogRef.updateSize('800px', '600px');
    }
    
    // Trigger TinyMCE resize after a short delay
    setTimeout(() => {
      if (this.editorInstance) {
        this.editorInstance.execCommand('mceAutoResize');
      }
    }, 100);
  }

  hasChanges(): boolean {
    return this.notesForm.value.notes !== this.initialContent;
  }
}
